import { RefObject, useImperativeHandle, useState } from 'react';
import {
  Badge,
  Button,
  Collapse,
  Fieldset,
  Flex,
  Group,
  LoadingOverlay,
  Modal,
  NumberInput,
  Radio,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import type { CompulsoryInsurancePenaltyTicketSummary } from '#/components/ui/tickets/compulsory-insurance-penalty/model';
import { ConfirmChargeAmountDialog } from '#/components/ui/tickets/components/ui/ConfirmChargeAmountDialog';
import { TICKET_TYPE, TICKET_TYPE_CONFIG } from '../../../config';
import type {
  FillQueryResultDialogRef,
  FillQueryResultDialogResult,
  FillQueryResultValue,
  QueryResultOption,
} from './useFillQueryResultDialog';

const CONFIG =
  TICKET_TYPE_CONFIG[TICKET_TYPE.COMPULSORY_INSURANCE_PENALTY_TICKETS];

const InitValue: FillQueryResultValue = {
  type: 'no_fee',
  amount: '',
  handlingFee: '13',
  reason: '',
};

type ConfirmResult = FillQueryResultDialogResult | undefined;

export function FillQueryResultDialog({
  modalRef,
}: {
  modalRef: RefObject<FillQueryResultDialogRef | null>;
}) {
  const [modelState, setModelState] = useState<
    | {
        ticket: CompulsoryInsurancePenaltyTicketSummary;
        onConfirm: () => void;
        onCancel: () => void;
      }
    | undefined
  >();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [queryResultValue, setQueryResultValue] =
    useState<FillQueryResultValue>(InitValue);

  const resetQueryResult = () => setQueryResultValue(InitValue);

  useImperativeHandle(
    modalRef,
    () => ({
      queryResultValue,
      open: async (ticket: CompulsoryInsurancePenaltyTicketSummary) => {
        const close = () => {
          setModelState(undefined);
          resetQueryResult();
          setIsSubmitting(false);
          setShowConfirm(false);
        };
        const promise = new Promise<ConfirmResult>((resolve) => {
          setModelState({
            ticket,
            onConfirm: () => {
              setIsSubmitting(true);
              const qrv = modalRef.current?.queryResultValue;
              if (!qrv) return;

              const { type, amount, handlingFee, reason } = qrv;

              if (type === 'fee_required') {
                const numberAmount = Number(amount);
                const numberHandlingFee = Number(handlingFee);
                resolve({
                  type,
                  amount: Number.isNaN(numberAmount) ? 0 : numberAmount,
                  handlingFee: Number.isNaN(numberHandlingFee)
                    ? 0
                    : numberHandlingFee,
                });
                return;
              }
              if (type === 'no_fee') {
                resolve({ type });
                return;
              }
              if (type === 'query_failed') {
                resolve({ type, reason });
                return;
              }
            },
            onCancel: () => {
              resolve(undefined);
              close();
            },
          });
        });

        const result = await promise;
        return [result, close] as const;
      },
    }),
    [modalRef, queryResultValue],
  );

  const setReason = (reason: string) =>
    setQueryResultValue((v) => ({ ...v, reason }));

  if (!modelState) return null;

  const { ticket, onConfirm, onCancel } = modelState;
  const subtitle = `${ticket.account.email}．${CONFIG.name}．${ticket.plateNumber}`;

  const numberAmount = Number(queryResultValue.amount);
  const confirmAmount = Number.isNaN(numberAmount) ? 0 : numberAmount;
  const numberHandlingFee = Number(queryResultValue.handlingFee);
  const confirmHandlingFee = Number.isNaN(numberHandlingFee)
    ? 0
    : numberHandlingFee;

  const handleSubmit = () => {
    if (queryResultValue.type === 'fee_required') {
      setShowConfirm(true);
      return;
    }
    onConfirm();
  };

  return (
    <>
      <Modal
        opened={!!ticket}
        onClose={onCancel}
        title={
          <Stack gap={2}>
            <Text fw={600} size="lg">
              回填查詢結果
            </Text>
            <Text size="sm" c="dimmed">
              {`${ticket.account.email}．${CONFIG.name}．${ticket.plateNumber}`}
            </Text>
          </Stack>
        }
        size="lg"
      >
        <LoadingOverlay visible={isSubmitting} />
        <Stack gap="md">
          {/* Platform link row */}
          <Group
            justify="space-between"
            bg="gray.0"
            p="sm"
            style={{ borderRadius: 8 }}
          >
            <Text size="sm" c="dimmed">
              線下查繳平台
            </Text>
            <Text size="sm" style={{ fontWeight: 600 }}>
              {CONFIG.queryPlatform}
            </Text>
          </Group>

          {/* Query result block */}
          <Fieldset
            variant="unstyled"
            legend={
              <Text size="sm" fw={500}>
                查詢結果{' '}
                <Text span c="red">
                  *
                </Text>
              </Text>
            }
          >
            <Radio.Group
              value={queryResultValue.type}
              onChange={(type) =>
                setQueryResultValue((v) => ({
                  ...v,
                  type: type as QueryResultOption,
                }))
              }
            >
              <Stack gap="sm">
                {/* 無需繳費 */}
                <Radio value="no_fee" label="無需繳費" />
                {/* 需繳費 */}
                <Stack gap="xs">
                  <Radio value="fee_required" label="需繳費" />
                  <Collapse expanded={queryResultValue.type === 'fee_required'}>
                    <Stack
                      gap="xs"
                      pl="md"
                      ml="xs"
                      style={{
                        borderLeft: '2px solid var(--mantine-color-gray-3)',
                      }}
                    >
                      <NumberInput
                        value={queryResultValue.amount}
                        onChange={(amount) => {
                          if (!amount) return;
                          setQueryResultValue((v) => ({
                            ...v,
                            amount: amount.toString(),
                          }));
                        }}
                        label={
                          <Text size="sm">
                            線上可繳金額 (NT$){' '}
                            <Text span c="red">
                              *
                            </Text>
                          </Text>
                        }
                        placeholder="輸入金額"
                        min={1}
                      />
                      <NumberInput
                        value={queryResultValue.handlingFee}
                        onChange={(handlingFee) => {
                          setQueryResultValue((v) => ({
                            ...v,
                            handlingFee: handlingFee.toString(),
                          }));
                        }}
                        label={
                          <Text size="sm">
                            手續費 (NT$){' '}
                            <Text span c="red">
                              *
                            </Text>
                          </Text>
                        }
                        placeholder="輸入手續費"
                        min={0}
                      />
                      <Text size="xs" c="dimmed">
                        送出後系統會即時向用戶發起請款，下一步顯示請款結果。
                      </Text>
                    </Stack>
                  </Collapse>
                </Stack>

                {/* 查詢失敗 */}
                <Stack gap="xs">
                  <Radio
                    value="query_failed"
                    label="查詢失敗"
                    description="本票直接結案，系統將寄信引導用戶更新資料"
                  />
                  <Collapse expanded={queryResultValue.type === 'query_failed'}>
                    <Stack
                      gap="xs"
                      pl="md"
                      ml="xs"
                      style={{
                        borderLeft: '2px solid var(--mantine-color-gray-3)',
                      }}
                    >
                      <Textarea
                        label="原因"
                        placeholder="Input placeholder"
                        value={queryResultValue.reason}
                        onChange={(e) => setReason(e.target.value)}
                      />
                      <Flex gap="xs">
                        <Badge
                          onClick={() =>
                            setReason(
                              '車號與身份證號／統編不匹配，故無法查詢違反強制險罰鍰',
                            )
                          }
                        >
                          查不到
                        </Badge>
                        <Badge onClick={() => setReason('車牌未登記')}>
                          車牌未登記
                        </Badge>
                      </Flex>
                    </Stack>
                  </Collapse>
                </Stack>
              </Stack>
            </Radio.Group>
          </Fieldset>

          {/* Footer */}
          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={onCancel}>
              取消
            </Button>
            <Button
              disabled={!queryResultValue}
              onClick={handleSubmit}
              loading={isSubmitting}
            >
              送出請款
            </Button>
          </Group>
        </Stack>
      </Modal>

      <ConfirmChargeAmountDialog
        opened={showConfirm}
        amount={confirmAmount}
        handlingFee={confirmHandlingFee}
        subtitle={subtitle}
        loading={isSubmitting}
        onBack={() => setShowConfirm(false)}
        onConfirm={onConfirm}
      />
    </>
  );
}
